import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  HiOutlineCheck,
  HiOutlineCloudArrowUp,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineXMark,
} from "react-icons/hi2";
import {
  fiveSService,
  plantService,
  areaService,
  masterDataService,
  authService,
  uploadService,
} from "../services";
import "./FiveSEntry.css";

const FiveSEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    auditDate: new Date().toISOString().split("T")[0],
    plantId: "",
    departmentId: "",
    areaId: "",
    auditor: "",
    notes: "",
  });

  const [items, setItems] = useState([]);

  // Initialize items with standard questions if new
  // Initialize items with standard questions if new
  useEffect(() => {
    if (!isEdit && items.length === 0) {
      loadChecklistMaster();
    }
  }, [isEdit]);

  const loadChecklistMaster = async () => {
    try {
      const master = await fiveSService.getChecklistMaster();
      const initialItems = [];
      Object.keys(master).forEach((category) => {
        master[category].forEach((question) => {
          initialItems.push({
            category,
            question,
            score: 0,
            remarks: "",
          });
        });
      });
      setItems(initialItems);
    } catch (error) {
      console.error("Error loading checklist master", error);
    }
  };

  const [plants, setPlants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    loadMasters();
    if (isEdit) {
      loadAudit();
    }
  }, [id]);

  const loadMasters = async () => {
    try {
      const tenantId = authService.getTenantId();

      const plantsData = await plantService.getAllPlants();
      setPlants(plantsData);

      const areasData = await areaService.getAllAreas();
      setAreas(areasData);

      if (masterDataService.getDepartments) {
        const depts = await masterDataService.getDepartments(tenantId);
        setDepartments(depts);
      }

      // Auto-select plant if only 1 exists (for new audits)
      if (!isEdit && plantsData?.length === 1) {
        setForm(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }
    } catch (error) {
      console.error("Error loading masters", error);
    }
  };

  const loadAudit = async () => {
    try {
      const data = await fiveSService.getAuditById(id);
      if (!data) return; // Handle case where audit is not found

      setForm({
        auditDate: data.auditDate
          ? data.auditDate.split("T")[0]
          : new Date().toISOString().split("T")[0],
        plantId: data.plantId || "",
        departmentId: data.departmentId || "",
        areaId: data.areaId || "",
        auditor: data.auditor || "",
        notes: data.notes || "",
      });

      // If existing audit has items, load them
      const itemsToLoad = data.items || data.checklistItems;
      if (itemsToLoad && itemsToLoad.length > 0) {
        setItems(itemsToLoad);
      } else {
        // Fallback for old audits - re-initialize standard questions
        loadChecklistMaster();
      }
    } catch (error) {
      console.error("Error loading audit", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    // If score changed, ensure it's number
    if (field === "score") {
      newItems[index].score = parseInt(value) || 0;
    }
    setItems(newItems);
  };

  const calculateAverage = (category) => {
    const categoryItems = items.filter((x) => x.category === category);
    if (categoryItems.length === 0) return 0;
    const sum = categoryItems.reduce((acc, curr) => acc + curr.score, 0);
    return (sum / categoryItems.length).toFixed(1);
  };

  const handleImageUpload = async (index, type, file) => {
    if (!file) return;
    try {
      const response = await uploadService.uploadFile(file);
      // Response expected: { url: '...' }
      if (response && response.url) {
        handleItemChange(
          index,
          type === "before" ? "imageBefore" : "imageAfter",
          response.url
        );
      } else {
        throw new Error("Invalid response from upload service");
      }
    } catch (error) {
      console.warn(
        "Upload failed, falling back to local base64 storage",
        error
      );
      // Fallback: Convert to Base64 and store directly in the field
      // This allows the app to function even if the upload endpoint is missing/broken
      const reader = new FileReader();
      reader.onloadend = () => {
        handleItemChange(
          index,
          type === "before" ? "imageBefore" : "imageAfter",
          reader.result
        );
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calculate total score
      const totalScore = items.reduce(
        (sum, item) => sum + (item.score || 0),
        0
      );

      // Find selected plant and area to store full objects (needed for list view)
      const selectedPlant = plants.find((p) => p.plantId == form.plantId);
      const selectedArea = areas.find((a) => a.areaId == form.areaId);

      const payload = {
        ...form,
        plantId: form.plantId ? parseInt(form.plantId) : null,
        departmentId: form.departmentId ? parseInt(form.departmentId) : null,
        areaId: form.areaId ? parseInt(form.areaId) : null,
        items: items,
        totalScore,
        plant: selectedPlant,
        area: selectedArea,
      };

      if (isEdit) {
        await fiveSService.updateAudit(id, payload);
      } else {
        await fiveSService.createAudit(payload);
      }
      navigate("/home/fives");
    } catch (error) {
      console.error(error);
      alert("Failed to save audit. Please check all fields.");
    }
  };

  return (
    <div className="audit-entry-container">
      <div className="audit-page-header">
        <h1>{isEdit ? "Edit" : "New"} 5S Audit</h1>
      </div>

      <form onSubmit={handleSubmit} className="audit-form">
        {/* Header Info */}
        <div className="audit-header-grid">
          <div className="form-group">
            <label className="form-label required">Audit Date</label>
            <input
              type="date"
              name="auditDate"
              value={form.auditDate}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label required">Auditor Name</label>
            <input
              type="text"
              name="auditor"
              value={form.auditor}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Plant</label>
            <select
              name="plantId"
              value={form.plantId}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">Select Plant</option>
              {plants.map((p) => (
                <option key={p.plantId} value={p.plantId}>
                  {p.plantName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Department</label>
            <select
              name="departmentId"
              value={form.departmentId}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">Select Department</option>
              {departments.map((d) => (
                <option key={d.departmentId} value={d.departmentId}>
                  {d.departmentName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Area</label>
            <select
              name="areaId"
              value={form.areaId}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">Select Area</option>
              {areas
                .filter(
                  (a) =>
                    !form.plantId || !a.plantId || a.plantId == form.plantId
                )
                .map((a) => (
                  <option key={a.areaId} value={a.areaId}>
                    {a.areaName}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Audit Checklist */}
        {[...new Set(items.map((i) => i.category))].map((category) => (
          <div key={category} className="audit-section">
            <div className="audit-section-header">
              <h3>{category}</h3>
              <div className="audit-score-badge">
                Average: {calculateAverage(category)}
              </div>
            </div>

            <div className="audit-table-wrapper">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th style={{ width: "30%" }}>Checklist Item</th>
                    <th className="text-center" style={{ width: "10%" }}>Score (0-5)</th>
                    <th className="text-center" style={{ width: "15%" }}>Check Before</th>
                    <th className="text-center" style={{ width: "15%" }}>Check After</th>
                    <th style={{ width: "15%" }}>Remarks</th>
                    <th style={{ width: "15%" }}>Action Required</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => {
                    if (item.category !== category) return null;
                    return (
                      <tr key={index}>
                        <td className="audit-question-cell">{item.question}</td>
                        <td className="audit-score-cell text-center">
                          <select
                            value={item.score}
                            onChange={(e) =>
                              handleItemChange(index, "score", e.target.value)
                            }
                            className={`audit-select-score ${
                              item.score >= 4
                                ? "score-high"
                                : item.score >= 2
                                ? "score-mid"
                                : "score-low"
                            }`}
                          >
                            {[0, 1, 2, 3, 4, 5].map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="audit-image-cell text-center">
                          {item.imageBefore ? (
                            <div className="image-preview-wrapper">
                              <div className="image-preview-container">
                                <a
                                  href={item.imageBefore}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="thumb-link"
                                >
                                  <img
                                    src={item.imageBefore}
                                    alt="Before"
                                    className="audit-thumb"
                                  />
                                  <div className="thumb-overlay">
                                    <HiOutlineArrowTopRightOnSquare />
                                  </div>
                                </a>
                                <button
                                  type="button"
                                  className="remove-image-btn"
                                  onClick={() =>
                                    handleItemChange(index, "imageBefore", "")
                                  }
                                >
                                  <HiOutlineXMark />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="custom-file-upload">
                              <label htmlFor={`before-${index}`} className="upload-label">
                                <HiOutlineCloudArrowUp className="upload-icon" />
                                <span>Upload</span>
                              </label>
                              <input
                                id={`before-${index}`}
                                type="file"
                                accept="image/*"
                                className="hidden-file-input"
                                onChange={(e) =>
                                  handleImageUpload(
                                    index,
                                    "before",
                                    e.target.files[0]
                                  )
                                }
                              />
                            </div>
                          )}
                        </td>
                        <td className="audit-image-cell text-center">
                          {item.imageAfter ? (
                            <div className="image-preview-wrapper">
                              <div className="image-preview-container">
                                <a
                                  href={item.imageAfter}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="thumb-link"
                                >
                                  <img
                                    src={item.imageAfter}
                                    alt="After"
                                    className="audit-thumb"
                                  />
                                  <div className="thumb-overlay">
                                    <HiOutlineArrowTopRightOnSquare />
                                  </div>
                                </a>
                                <button
                                  type="button"
                                  className="remove-image-btn"
                                  onClick={() =>
                                    handleItemChange(index, "imageAfter", "")
                                  }
                                >
                                  <HiOutlineXMark />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="custom-file-upload">
                              <label htmlFor={`after-${index}`} className="upload-label">
                                <HiOutlineCloudArrowUp className="upload-icon" />
                                <span>Upload</span>
                              </label>
                              <input
                                id={`after-${index}`}
                                type="file"
                                accept="image/*"
                                className="hidden-file-input"
                                onChange={(e) =>
                                  handleImageUpload(
                                    index,
                                    "after",
                                    e.target.files[0]
                                  )
                                }
                              />
                            </div>
                          )}
                        </td>
                        <td className="audit-remarks-cell">
                          <input
                            type="text"
                            value={item.remarks}
                            onChange={(e) =>
                              handleItemChange(index, "remarks", e.target.value)
                            }
                            placeholder="Optional remarks"
                            className="audit-input-remarks"
                          />
                        </td>
                        <td className="audit-remarks-cell">
                          {item.score < 3 && (
                            <input
                              type="text"
                              value={item.actionRequired || ""}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "actionRequired",
                                  e.target.value
                                )
                              }
                              placeholder="Action Required"
                              className="audit-input-remarks"
                              style={{ border: "1px solid #ff6b6b" }}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <div className="audit-notes-section">
          <label>Overall Notes / Initial Observations</label>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            className="audit-textarea"
            placeholder="Enter any additional observations or summary here..."
          />
        </div>

        <div className="audit-actions-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate("/home/fives")}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save Audit
          </button>
        </div>
      </form>
    </div>
  );
};

export default FiveSEntry;
